import { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import OpportunityBoard from '../components/opportunities/OpportunityBoard';
import OpportunityTable from '../components/opportunities/OpportunityTable';
import OpportunityDrawer from '../components/opportunities/OpportunityDrawer';
import { api } from '../lib/api';

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export default function Opportunities() {
  const [view, setView] = useState<'board' | 'table'>('board');
  const [pipelines, setPipelines] = useState<Array<{ pipeline_id: string; pipeline_name: string }>>([]);
  const [pipeline, setPipeline] = useState('');
  const [board, setBoard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [sortKey, setSortKey] = useState<'value' | 'age' | 'updated'>('updated');
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [personaFilter, setPersonaFilter] = useState<string | null>(null);

  // bootstrap pipeline list from the existing summary endpoint
  useEffect(() => { (async () => {
    try {
      const res = await api.listOpportunities({ limit: '1' });
      const list = res.summary?.by_pipeline || [];
      setPipelines(list); setPipeline((p) => p || list[0]?.pipeline_id || '');
    } catch (e: any) {
      setError(e?.message || 'Failed to load pipelines. Please refresh the page.');
    }
  })(); }, []);

  const loadBoard = useCallback(async () => {
    if (!pipeline) return;
    setLoading(true);
    setError(null);
    try {
      setBoard(await api.boardOpportunities(pipeline, showClosed ? 'all' : 'open'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load board. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pipeline, showClosed]);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  // OO6a: this board READS. Same fix as OO1b on Deals.tsx - the mount POST to
  // /ghl-sync/opportunities/refresh is gone, because the server's own 15-minute
  // opportunity-sync cron already refreshes the mirror and auto-promotes under-contract
  // opps. Opening a list must not write.

  // reset persona filter when the pipeline/board changes
  useEffect(() => { setPersonaFilter(null); }, [pipeline]);

  const onMove = async (oppId: string, toStageId: string) => {
    const prev = board;
    // optimistic: relocate the opp + recompute counts/values
    setBoard((b: any) => {
      if (!b) return b;
      let moved: any = null;
      const stages = b.stages.map((s: any) => {
        const keep = s.opportunities.filter((o: any) => { if (o.id === oppId) { moved = o; return false; } return true; });
        return { ...s, opportunities: keep };
      });
      if (moved) { moved = { ...moved, stage_id: toStageId }; }
      const next = stages.map((s: any) => s.id === toStageId && moved ? { ...s, opportunities: [moved, ...s.opportunities] } : s)
        .map((s: any) => ({ ...s, count: s.opportunities.length, value: s.opportunities.reduce((a: number, o: any) => a + (Number(o.monetary_value) || 0), 0) }));
      return { ...b, stages: next };
    });
    try { await api.updateOpportunity(oppId, { pipelineStageId: toStageId }); }
    catch (e: any) { setBoard(prev); setToast(e?.message || 'Could not move deal — reverted'); setTimeout(() => setToast(null), 4000); }
  };

  const seed = async () => {
    if (!pipeline) return;
    setSeeding(true);
    try {
      await api.seedBoardPersonas(pipeline);
      await loadBoard();
    } catch (e: any) {
      setToast(e?.message || 'Seed failed');
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSeeding(false);
    }
  };

  // Derive unique non-null persona labels from all board cards for the filter chip row
  const personaLabels = useMemo<string[]>(() => {
    if (!board?.stages) return [];
    const seen = new Set<string>();
    for (const stage of board.stages) {
      for (const opp of stage.opportunities) {
        if (opp.persona_label) seen.add(opp.persona_label);
      }
    }
    return Array.from(seen).sort();
  }, [board]);

  return (
    <Layout>
      <div>
        <div className="max-w-[1700px] mx-auto py-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Opportunities</h1>
            {board && <p className="text-gray-500 mt-0.5 text-sm">{board.summary.open_count} open · {money(board.summary.open_value)} in {board.pipeline.name}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={pipeline} onChange={(e) => setPipeline(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {pipelines.map((p) => <option key={p.pipeline_id} value={p.pipeline_id}>{p.pipeline_name}</option>)}
            </select>
            <label className="text-sm text-gray-600 flex items-center gap-1">
              <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show won/lost
            </label>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value as 'value' | 'age' | 'updated')} className="px-2 py-2 border rounded-lg text-sm">
              <option value="updated">Recently updated</option>
              <option value="value">Value</option>
              <option value="age">Age in stage</option>
            </select>
            <button
              onClick={seed}
              disabled={seeding || !pipeline}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {seeding ? 'Seeding…' : 'Seed personas'}
            </button>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              <button onClick={() => setView('board')} className={`px-3 py-2 ${view === 'board' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600'}`}>Board</button>
              <button onClick={() => setView('table')} className={`px-3 py-2 ${view === 'table' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600'}`}>Table</button>
            </div>
          </div>
        </div>

        {/* Persona filter chip row — shown only when there are labelled cards */}
        {personaLabels.length > 0 && view === 'board' && (
          <div className="max-w-[1700px] mx-auto pb-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400 shrink-0">Filter:</span>
            <button
              onClick={() => setPersonaFilter(null)}
              className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                personaFilter === null
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              All
            </button>
            {personaLabels.map((label) => (
              <button
                key={label}
                onClick={() => setPersonaFilter((cur) => (cur === label ? null : label))}
                className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                  personaFilter === label
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-[1700px] mx-auto py-6">
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-700 ml-2">✕</button>
          </div>
        )}
        {loading ? <div className="text-center py-12 text-gray-500">Loading...</div>
          : view === 'board' && board
            ? <OpportunityBoard
                stages={board.stages}
                brokers={board.brokers}
                onCardClick={setDrawerId}
                onMove={onMove}
                sortKey={sortKey}
                personaFilter={personaFilter}
              />
            : <OpportunityTable
                opps={(board?.stages || []).flatMap((s: any) => s.opportunities)}
                loading={false} onRow={setDrawerId}
                sortBy="name" sortDir="asc" onSort={() => {}} />}
      </div>

      {drawerId && <OpportunityDrawer id={drawerId} onClose={() => setDrawerId(null)} onSaved={loadBoard} />}
      {toast && <div className="fixed bottom-[calc(1rem+var(--tab-bar-space,0px))] right-4 bg-red-600 text-white px-4 py-2 rounded-lg text-sm shadow-lg">{toast}</div>}
    </Layout>
  );
}
